/**
 * Cliente HTTP del backend real (everex-traza-back).
 *
 * Maneja: base URL (VITE_API_BASE_URL), serialización JSON, token Bearer en
 * localStorage y normalización de errores a `ApiError`.
 *
 * Hoy lo consume solo el endpoint de Auth. Los demás endpoints siguen contra el
 * mock in-memory hasta que se integren uno por uno (estrategia del Handoff).
 */

/** Error de API con código estable — mismo shape que usaban los endpoints mock. */
export class ApiError extends Error {
  public readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
const BASE_URL = (API_BASE_URL ?? '').replace(/\/$/, '');
const TOKEN_KEY = 'riga-traza-token-v1';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* localStorage no disponible — ignorar */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignorar */
  }
}

// ── Hook de expiración de sesión ────────────────────────────────────────────
// Se dispara cuando el backend rechaza el token (401 AUTH_TOKEN_EXPIRED /
// AUTH_INVALID_TOKEN). El session store se registra al inicializarse y resetea
// el estado local → ProtectedRoute redirige a /login.
// Usamos un setter en vez de import directo del store para evitar acoplar
// el cliente HTTP al store y mantener el orden de inicialización limpio.
type SessionExpiredHandler = () => void;
let sessionExpiredHandler: SessionExpiredHandler | null = null;

export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  sessionExpiredHandler = handler;
}

/** Códigos de error del backend que indican "este token ya no sirve" o que el
 *  cliente cree estar logueado pero no tiene token válido para mandar. */
const TOKEN_INVALID_CODES = new Set([
  'AUTH_TOKEN_EXPIRED',
  'AUTH_INVALID_TOKEN',
  'AUTH_MISSING_TOKEN',
]);

interface ApiFetchOptions {
  method?: string;
  /** Body a serializar como JSON. */
  body?: unknown;
  /** Adjuntar `Authorization: Bearer <token>` si hay token. */
  auth?: boolean;
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: ApiFetchOptions = {},
): Promise<T> {
  const { method = 'GET', body, auth = false } = opts;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth) {
    const token = getToken();
    if (token) headers['authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('No se pudo conectar con el servidor', 'NETWORK_ERROR');
  }

  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const payload = (data ?? {}) as { error?: string; message?: string; code?: string };
    const code = payload.code ?? `HTTP_${res.status}`;

    // Token rechazado por el backend → forzar logout local. El caller igual
    // recibe el ApiError; en paralelo el store resetea usuario y ProtectedRoute
    // dispara el redirect a /login.
    if (res.status === 401 && TOKEN_INVALID_CODES.has(code)) {
      clearToken();
      if (sessionExpiredHandler) {
        sessionExpiredHandler();
      } else {
        // Si el handler no está registrado significa que session.ts no se cargó
        // antes del primer fetch — bug de orden de inicialización. Loguear para
        // que sea fácil de detectar.
        console.warn(
          '[apiFetch] 401 ' + code + ' pero sessionExpiredHandler no está registrado. ' +
            'Forzando redirect duro a /login.',
        );
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
    }

    throw new ApiError(payload.error ?? payload.message ?? `Error ${res.status}`, code);
  }

  return data as T;
}
